/**
 * Reading a place that was typed or pasted (the owner, 2026-10-26: "below the map, paste the coordinates, comma separated, or the Google Maps share link, and the map moves there").
 * Accepts: "28.4000541, 77.1035941" (comma, space or semicolon; brackets and N/E marks are fine), degrees-minutes-seconds as Google shows them, and a Google Maps link of the long kind
 * (@lat,lng · !3dlat!4dlng · q= / ll= / query= / destination= / center= · /search/lat,lng · /place/lat,lng). A SHORT link (maps.app.goo.gl/..., goo.gl/maps/...) holds no
 * numbers: it is returned as `short` for the server to open (the function resolve-map-link). Nothing here touches the network.
 */
export type PinRead =
  | { ok: true; lat: number; lng: number; swapped: boolean }
  | { ok: false; short?: string; why: string };

const DEC = String.raw`[-+]?\d{1,3}\.\d+`;
const SHORT_HOST = /^(?:https?:\/\/)?(?:maps\.app\.goo\.gl|goo\.gl\/maps|g\.co\/kgs|share\.google)(?:[/?#]|$)/i;
const GOOGLE_MAPS = /(?:google\.[a-z.]+\/maps|maps\.google\.[a-z.]+|maps\.apple\.com)/i;

const inIndia = (lat: number, lng: number) => lat >= 6 && lat <= 38 && lng >= 68 && lng <= 98;
const valid = (lat: number, lng: number) => Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0);

/** The two numbers in the order they were given, turned the right way round when they are clearly longitude first (the wrong way round is a common paste). */
function settle(a: number, b: number): PinRead {
  if (inIndia(a, b)) return { ok: true, lat: a, lng: b, swapped: false };
  if (inIndia(b, a)) return { ok: true, lat: b, lng: a, swapped: true };
  if (!valid(a, b)) return { ok: false, why: 'Those numbers are not a place on the map. Latitude comes first (about 26 for Jodhpur), then longitude (about 73).' };
  return { ok: true, lat: a, lng: b, swapped: false };
}

const dms = (d: string, m: string, s: string, hemi: string) => {
  const v = Number(d) + Number(m) / 60 + Number(s || 0) / 3600;
  return /[SW]/i.test(hemi) ? -v : v;
};

export function parsePin(input: string): PinRead {
  const raw = String(input ?? '').trim();
  if (!raw) return { ok: false, why: 'Paste coordinates or a Google Maps link first.' };
  if (SHORT_HOST.test(raw.split(/\s+/)[0] ?? '')) return { ok: false, short: raw.split(/\s+/)[0]!.replace(/^(?!https?:\/\/)/i, 'https://'), why: 'That is a short Google link; opening it.' };

  let text = raw;
  try { text = decodeURIComponent(raw); } catch { /* a stray % is left as it is */ }
  text = text.replace(/−/g, '-').replace(/[′’]/g, "'").replace(/[″”]/g, '"');

  // A link: the place is in the path or the query, in the order Google writes it.
  if (/^https?:\/\//i.test(text) || GOOGLE_MAPS.test(text)) {
    const at = text.match(new RegExp(String.raw`@(${DEC}),(${DEC})`));
    if (at) return settle(Number(at[1]), Number(at[2]));
    const bang = text.match(new RegExp(String.raw`!3d(${DEC})!4d(${DEC})`));
    if (bang) return settle(Number(bang[1]), Number(bang[2]));
    const query = text.match(new RegExp(String.raw`[?&](?:q|ll|query|destination|daddr|center|sll|viewpoint)=(${DEC})\s*,\s*(${DEC})`));
    if (query) return settle(Number(query[1]), Number(query[2]));
    const path = text.match(new RegExp(String.raw`/(?:search|place|dir)/(?:[^/]*/)?(${DEC}),\+?(${DEC})`));
    if (path) return settle(Number(path[1]), Number(path[2]));
    return { ok: false, why: 'That link has no exact spot in it (it names a place, not a point). In Google Maps, press and hold on the spot, then copy the numbers shown, or share the pin.' };
  }

  // Degrees, minutes, seconds: 28°24'00.2"N 77°06'13.3"E
  const d = text.match(/(\d{1,3})\s*°\s*(\d{1,2})\s*'\s*([\d.]+)?\s*"?\s*([NSns])[\s,;]*(\d{1,3})\s*°\s*(\d{1,2})\s*'\s*([\d.]+)?\s*"?\s*([EWew])/);
  if (d) return settle(dms(d[1]!, d[2]!, d[3] ?? '0', d[4]!), dms(d[5]!, d[6]!, d[7] ?? '0', d[8]!));

  // Two decimals: 28.4000541, 77.1035941 · (28.4, 77.1) · 28.4000541 N 77.1035941 E · 28.4000541;77.1035941
  const pair = text.match(new RegExp(String.raw`(${DEC})\s*°?\s*([NSns])?\s*[,;\s]\s*(${DEC})\s*°?\s*([EWew])?`));
  if (pair) {
    const a = Number(pair[1]) * (/[Ss]/.test(pair[2] ?? '') ? -1 : 1);
    const b = Number(pair[3]) * (/[Ww]/.test(pair[4] ?? '') ? -1 : 1);
    return settle(a, b);
  }
  return { ok: false, why: 'Paste two numbers like 26.2389, 73.0243, or a Google Maps link.' };
}
